/**
 *
 * dropzones or fields lengt can be upto 2
 * 2 for operator between and 1 for rest of the operatror
 *
 * so its good to use indexing instead of any iterator function
 */

import { Box, Tooltip } from '@mui/material';
import CloseRoundedIcon from '@mui/icons-material/CloseRounded';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import React, { Dispatch, SetStateAction, useEffect, useMemo, useState } from 'react';
import EvaluationRightOperandDropZone from '../../DropZones/EvaluationRightOperandDropZone';
import { useDispatch } from 'react-redux';
import {
  addField,
  deleteField,
  setRightOperand,
} from '../../../../redux/Calculations/CalculationsActions';
import InputField from './InputField';
import { useSelector } from 'react-redux';
import { RootState } from '../../../../redux';
import { fontSize, palette } from '../../../..';
import { isFlowFilledProperly } from '../../utils';
import { NotificationDialog } from '../../../CommonFunctions/DialogComponents';
import SelectedParameter from '../../CalculationControlPanel/SelectedParameter';
import SelectParameter from '../../../ChartFieldFilter/SelectParameter';
import { checkIfMissingColumn } from '../../../CommonFunctions/CommonFunctions';
interface FieldOrDropZonesProps {
  rightOperand: (string | number | null)[];
  rightOperandType: string[];
  disabled?: boolean;
  isBetween: boolean;
  leftOperandType: string;
  conditionFilterId: string;
  filterIdx: number;
  propKey: string;
  timeGrain: string;
  paramSource: any;
  setSource: Dispatch<SetStateAction<any>>;
  parameterized1: boolean;
  setParameterized1: Dispatch<SetStateAction<boolean>>;
  parameterized2: boolean;
  setParameterized2: Dispatch<SetStateAction<boolean>>;
}
const FieldOrDropZones = ({
  rightOperand,
  rightOperandType,
  disabled,
  isBetween,
  leftOperandType,
  conditionFilterId,
  filterIdx,
  propKey,
  timeGrain,
  paramSource,
  setSource,
  parameterized1,
  setParameterized1,
  parameterized2,
  setParameterized2,
}: FieldOrDropZonesProps) => {
  const { currentCalculationSession, chartProperties, tabTileProps } = useSelector(
    (state: RootState) => {
      return {
        currentCalculationSession: state.calculations.properties[propKey].currentCalculationSession,
        chartProperties: state.chartProperties,
        tabTileProps: state.tabTileProps,
      };
    },
  );
  const dispatch = useDispatch();

  const fields = currentCalculationSession?.calculationInfo.fields;
  const selectedDs = chartProperties.properties[propKey].selectedDs;

  const [isShowWarning, setIsShowWarning] = useState([false, false]);

  const [alert, setAlert] = useState<any>(null);
  const showIsBetween = useMemo(() => {
    if (!currentCalculationSession) return false;
    if (!isBetween) {
      return false;
    }

    if (isBetween && parameterized1) {
      return false;
    }
    if (
      isBetween &&
      currentCalculationSession.calculationInfo.conditionFilters[conditionFilterId as string][0]
        .conditions[filterIdx as number].rightOperand.length > 1 &&
      currentCalculationSession.calculationInfo.conditionFilters[conditionFilterId as string][0]
        .conditions[filterIdx as number].parameterId.length === 1
    ) {
      return false;
    }
    return true;
  }, [isBetween, parameterized1, currentCalculationSession]);

  const getType = () => {
    if (['decimal', 'integer'].includes(leftOperandType)) return 'number';
    if (['date', 'timestamp'].includes(leftOperandType))
      return timeGrain === 'date' ? 'date' : 'text';
    return 'text';
  };

  const updateRightOperand = (index: 0 | 1, value: string, valueType: string) => {
    if (disabled) return;
    const updatedRightOperand = [...rightOperand];
    const updatedRightOperandType = [...rightOperandType];
    updatedRightOperand[index] = value;
    updatedRightOperandType[index] = valueType;
    dispatch(
      setRightOperand(
        propKey,
        conditionFilterId,
        filterIdx,
        updatedRightOperand,
        updatedRightOperandType,
      ),
    );
  };

  const handleDrop = (index: 0 | 1, value: string, valueType: string, valueInfo: any) => {
    if (disabled) return;
    if (!currentCalculationSession) return;
    if (valueType === 'flow') {
      if (!isFlowFilledProperly(currentCalculationSession.calculationInfo, value)) {
        setAlert({
          severity: 'warning',
          message: `Please ensure at least one field or flow is present and all conditions are met for flow  before dropping.`,
        });
        return;
      }
    }
    if (valueType === 'field')
      dispatch(
        addField(
          propKey,
          value,
          valueInfo.fieldname,
          valueInfo.dataType,
          valueInfo.displayname,
          valueInfo.tableId,
        ),
      );
    updateRightOperand(index, value, valueType);
  };

  const getName = (type: 'flow' | 'field', id: string) => {
    if (!currentCalculationSession) return null;
    if (type === 'flow') return currentCalculationSession.flowDisplayNames[id];
    else if (type === 'field')
      return currentCalculationSession.calculationInfo.fields[id]?.displayName;
  };

  const deleteRightOperand = (idx: 0 | 1) => {
    if (disabled) return;
    const updatedRightOperand = [...rightOperand];
    const updatedRightOperandType = [...rightOperandType];
    updatedRightOperand[idx] = null;
    updatedRightOperandType[idx] = 'blank';
    dispatch(
      setRightOperand(
        propKey,
        conditionFilterId,
        filterIdx,
        updatedRightOperand,
        updatedRightOperandType,
      ),
    );
    if (rightOperandType[idx] === 'field')
      dispatch(deleteField(propKey, rightOperand[idx] as string));
  };

  useEffect(() => {
    if (!fields || !currentCalculationSession) return;

    const checkWarning = async (sourceId: string) => {
      const field = fields[sourceId];
      if (!field) return false;
      try {
        const res = await checkIfMissingColumn(
          field,
          selectedDs,
          tabTileProps.tablesForSelectedDataSets[selectedDs.id],
        );
        return res.isMissing;
      } catch (error) {
        console.error('Error checking missing column:', error);
        return true;
      }
    };
    let isMounted = true;
    (async () => {
      const results = await Promise.all([
        checkWarning(rightOperand[0] as string),
        checkWarning(rightOperand[1] as string),
      ]);
      if (isMounted) setIsShowWarning([results[0], results[1]]);
    })();
    return () => {
      isMounted = false;
    };
  }, [rightOperand]);

  if (!currentCalculationSession) return null;

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        gap: '0.625rem',
        marginBottom: '0.625rem',
        width: '100%',
      }}
    >
      {
        /**
         * '0' or 0 */
        rightOperand[0] == 0 || rightOperand[0] ? (
          currentCalculationSession.calculationInfo.conditionFilters[conditionFilterId as string][0]
            .conditions[filterIdx as number].parameterId.length > 0 &&
          currentCalculationSession.calculationInfo.conditionFilters[conditionFilterId as string][0]
            .conditions[filterIdx as number].parameterId[0] !== null ? (
            <SelectedParameter
              propKey={propKey}
              conditionFilterId={conditionFilterId}
              filterIdx={filterIdx}
              paramSource={paramSource}
              setSource={setSource}
              isBetweenOperandIndex={0}
            />
          ) : (
            <Box
              sx={{
                height: '1.5rem',
                padding: '0.3rem',
                border: isShowWarning[0] ? '1px solid #850000ff' : '1px solid  #d3d3d3',
                display: 'flex',
                alignItems: 'center',
                borderRadius: '5px',
              }}
            >
              {disabled ? (
                <Tooltip
                  title="This calculation is being used in another flow. Please remove the dependency before deleting."
                  slotProps={{
                    tooltip: {
                      sx: {
                        fontSize: '12px',
                      },
                    },
                  }}
                >
                  <LockOutlinedIcon
                    style={{
                      color: '#2BB9BB',
                    }}
                  />
                </Tooltip>
              ) : (
                <CloseRoundedIcon
                  onClick={() => {
                    if (disabled) return;
                    deleteRightOperand(0);
                  }}
                  sx={{
                    fontSize: fontSize.medium,
                    cursor: disabled ? 'not-allowed' : 'pointer',
                    color: palette.primary.contrastText,
                    marginRight: '0.5rem',
                    '&:hover': {
                      color: 'red',
                      transform: 'scale(1.1)',
                    },
                  }}
                />
              )}
              {['flow', 'field'].includes(rightOperandType[0]) ? (
                <span>
                  {getName(rightOperandType[0] as 'flow' | 'field', rightOperand[0] as string)}
                </span>
              ) : (
                <InputField
                  disabled={disabled}
                  type={getType()}
                  isDecimal={leftOperandType === 'decimal'}
                  value={rightOperand[0] as string}
                  onBlur={(value) => updateRightOperand(0, value, rightOperandType[0])}
                />
              )}
            </Box>
          )
        ) : parameterized1 ? (
          <SelectParameter
            propKey={propKey}
            type="calculation"
            calculationSession={currentCalculationSession}
            filterIdx={filterIdx}
            conditionFilterId={conditionFilterId}
            addParameter={parameterized1}
            setAddParameter={setParameterized1}
            setSource={setSource}
          />
        ) : (
          <EvaluationRightOperandDropZone
            indicator={isBetween ? 'First' : undefined}
            leftOperandType={leftOperandType}
            onDrop={handleDrop}
            disabled={disabled}
          />
        )
      }

      {showIsBetween &&
        /**
         * '0' or 0 */
        (rightOperand[1] == 0 || rightOperand[1] ? (
          <Box
            sx={{
              height: '1.5rem',
              padding: '0.3rem',
              border: isShowWarning[1] ? '1px solid #850000ff' : '1px solid  #d3d3d3',
              display: 'flex',
              alignItems: 'center',
              borderRadius: '5px',
            }}
          >
            {disabled ? (
              <Tooltip
                title="This calculation is being used in another flow. Please remove the dependency before deleting."
                slotProps={{
                  tooltip: {
                    sx: {
                      fontSize: '12px',
                    },
                  },
                }}
              >
                <LockOutlinedIcon
                  style={{
                    color: '#2BB9BB',
                  }}
                />
              </Tooltip>
            ) : (
              <CloseRoundedIcon
                onClick={() => {
                  if (disabled) return;
                  deleteRightOperand(1);
                }}
                sx={{
                  fontSize: fontSize.medium,
                  cursor: disabled ? 'not-allowed' : 'pointer',
                  color: palette.primary.contrastText,
                  marginRight: '0.5rem',
                  '&:hover': {
                    color: 'red',
                    transform: 'scale(1.1)',
                  },
                }}
              />
            )}
            {['flow', 'field'].includes(rightOperandType[1]) ? (
              <span>
                {getName(rightOperandType[1] as 'flow' | 'field', rightOperand[1] as string)}
              </span>
            ) : (
              <InputField
                disabled={disabled}
                isDecimal={leftOperandType === 'decimal'}
                type={getType()}
                value={rightOperand[1] as string}
                onBlur={(value) => updateRightOperand(1, value, rightOperandType[1])}
              />
            )}
          </Box>
        ) : (
          <EvaluationRightOperandDropZone
            onDrop={handleDrop}
            indicator={isBetween ? 'Second' : undefined}
            leftOperandType={leftOperandType}
            disabled={disabled}
          />
        ))}
      {alert && (
        <NotificationDialog
          severity={alert.severity}
          openAlert={alert}
          onCloseAlert={() => setAlert(null)}
          testMessage={alert.message}
        />
      )}
    </Box>
  );
};

export default FieldOrDropZones;
